import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody, ModalHeader, Badge, Button, Form, FormGroup, Label, Input, Card, CardBody } from 'reactstrap';
import EventBus from 'eventing-bus';
import moment from 'moment';
import './FeedbackSidebar.css';

const FeedbackSidebar = ({ isOpen, onClose, feedbackId, filters }) => {
  const dispatch = useDispatch();
  const { selectedFeedback, loading } = useSelector((state) => state.Admin);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [adminResponse, setAdminResponse] = useState('');

  useEffect(() => {
    if (feedbackId && isOpen) {
      dispatch({ type: 'GET_ADMIN_FEEDBACK_BY_ID', payload: { id: feedbackId } });
    }
  }, [feedbackId, isOpen, dispatch]);

  useEffect(() => {
    if (selectedFeedback) {
      setStatusUpdate(selectedFeedback.status || '');
      setAdminResponse(selectedFeedback.adminResponse?.message || '');
    }
  }, [selectedFeedback]);

  const handleStatusUpdate = () => {
    if (!statusUpdate || statusUpdate === selectedFeedback.status) {
      EventBus.publish('warning', 'Please select a different status');
      return;
    }

    dispatch({
      type: 'UPDATE_FEEDBACK_STATUS',
      payload: {
        id: selectedFeedback._id,
        status: statusUpdate,
        updateSelected: true,
        filters: filters || {}
      }
    });
  };

  const handleAddResponse = () => {
    if (!adminResponse || adminResponse.trim().length === 0) {
      EventBus.publish('warning', 'Please enter a response message');
      return;
    }

    dispatch({
      type: 'ADD_ADMIN_FEEDBACK_RESPONSE',
      payload: {
        id: selectedFeedback._id,
        message: adminResponse.trim(),
        filters: filters || {}
      }
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'warning',
      'in-review': 'info',
      'resolved': 'success',
      'closed': 'secondary'
    };
    return colors[status] || 'secondary';
  };

  const getTypeColor = (type) => {
    const colors = {
      'issue': 'danger',
      'suggestion': 'success',
      'others': 'info'
    };
    return colors[type] || 'info';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'critical': 'danger',
      'high': 'danger',
      'medium': 'warning',
      'low': 'secondary'
    };
    return colors[priority] || 'secondary';
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} modalClassName='right right-without-bg' backdrop={false} className={`main-modal right right-side-modal style-three feedback-sidebar-modal`}>
      <ModalHeader toggle={onClose}>
        <h3><i className="fa fa-comments mr-2"></i> Feedback Details</h3>
      </ModalHeader>
      <ModalBody>
        <div className='filters-modal-content'>
          <div className='function-box'>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="text-muted mt-2">Loading feedback details...</p>
              </div>
            ) : selectedFeedback ? (
              <div className="feedback-details">
                {/* Header Section */}
                <Card className="mb-3 border-0 shadow-sm">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h5 className="mb-1">
                          <strong>{selectedFeedback.ticketId}</strong>
                        </h5>
                        <p className="text-muted mb-0 small">
                          <i className="fa fa-clock-o mr-1"></i>
                          {moment(selectedFeedback.createdAt).format('MMM DD, YYYY [at] hh:mm A')}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge color={getStatusColor(selectedFeedback.status)} className="mb-1 px-3 py-2">
                          {selectedFeedback.status}
                        </Badge>
                        <br />
                        <Badge color={getPriorityColor(selectedFeedback.priority)} className="px-3 py-1">
                          {selectedFeedback.priority} priority
                        </Badge>
                      </div>
                    </div>

                    <div className="d-flex align-items-center mb-2">
                      <Badge color={getTypeColor(selectedFeedback.type)} className="mr-2">
                        {selectedFeedback.type}
                      </Badge>
                      {selectedFeedback.emailSent && (
                        <Badge color="success" className="mr-2">
                          <i className="fa fa-check mr-1"></i> Email Sent
                        </Badge>
                      )}
                    </div>
                  </CardBody>
                </Card>

                {/* User & Workspace Info */}
                <Card className="mb-3 border-0 shadow-sm">
                  <CardBody>
                    <h6 className="font-weight-bold mb-3">
                      <i className="fa fa-user mr-2"></i>User Information
                    </h6>
                    <div className="info-row mb-2">
                      <span className="text-muted">Name:</span>
                      <strong>
                        {selectedFeedback.userId?.firstName} {selectedFeedback.userId?.lastName}
                      </strong>
                    </div>
                    <div className="info-row mb-2">
                      <span className="text-muted">Email:</span>
                      <strong>{selectedFeedback.email || selectedFeedback.userId?.email}</strong>
                    </div>
                    <div className="info-row">
                      <span className="text-muted">Workspace:</span>
                      <strong>{selectedFeedback.workspaceId?.name || 'N/A'}</strong>
                    </div>
                  </CardBody>
                </Card>

                {/* Feedback Content */}
                <Card className="mb-3 border-0 shadow-sm">
                  <CardBody>
                    <h6 className="font-weight-bold mb-3">
                      <i className="fa fa-file-text-o mr-2"></i>Feedback Details
                    </h6>
                    <div className="mb-3">
                      <Label className="text-muted small">Title</Label>
                      <h6>{selectedFeedback.title}</h6>
                    </div>
                    <div className="mb-3">
                      <Label className="text-muted small">Description</Label>
                      <p className="pre-wrap">{selectedFeedback.description}</p>
                    </div>
                  </CardBody>
                </Card>

                {/* Screenshot */}
                {selectedFeedback.screenshot && (
                  <Card className="mb-3 border-0 shadow-sm">
                    <CardBody>
                      <h6 className="font-weight-bold mb-3">
                        <i className="fa fa-image mr-2"></i>Screenshot
                      </h6>
                      <div className="text-center">
                        <img
                          src={selectedFeedback.screenshot}
                          alt="Feedback Screenshot"
                          className="img-fluid rounded"
                          style={{ maxHeight: '400px', cursor: 'pointer' }}
                          onClick={() => window.open(selectedFeedback.screenshot, '_blank')}
                        />
                      </div>
                      <p className="text-muted small mt-2">
                        <i className="fa fa-search-plus mr-1"></i>
                        Click image to view fullscreen
                      </p>
                    </CardBody>
                  </Card>
                )}

                

                {/* Status Update Section */}
                <Card className="mb-3 border-0 shadow-sm">
                  <CardBody>
                    <h6 className="font-weight-bold mb-3">
                      <i className="fa fa-flag mr-2"></i>Update Status
                    </h6>
                    <FormGroup>
                      <Label>Change Status</Label>
                      <Input
                        type="select"
                        value={statusUpdate}
                        onChange={(e) => setStatusUpdate(e.target.value)}
                      >
                        <option value="pending">Pending</option>
                        <option value="in-review">In Review</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </Input>
                    </FormGroup>
                    <Button
                      color="success"
                      onClick={handleStatusUpdate}
                      disabled={!statusUpdate || statusUpdate === selectedFeedback.status}
                      block
                    >
                      <i className="fa fa-check mr-2"></i>
                      Update Status
                    </Button>
                  </CardBody>
                </Card>

                {/* Admin Response Section */}
                <Card className="mb-3 border-0 shadow-sm">
                  <CardBody>
                    <h6 className="font-weight-bold mb-3">
                      <i className="fa fa-reply mr-2"></i>Admin Response
                    </h6>

                    {selectedFeedback.adminResponse?.message ? (
                      <div className="bg-light p-3 rounded mb-3">
                        <p className="mb-2 pre-wrap">{selectedFeedback.adminResponse.message}</p>
                        <small className="text-muted">
                          <i className="fa fa-user mr-1"></i>
                          {selectedFeedback.adminResponse.respondedBy?.firstName}{' '}
                          {selectedFeedback.adminResponse.respondedBy?.lastName}
                          {' '}- {moment(selectedFeedback.adminResponse.respondedAt).fromNow()}
                        </small>
                      </div>
                    ) : (
                      <p className="text-muted mb-3">No admin response yet</p>
                    )}

                    <FormGroup>
                      <Label>Add/Update Response</Label>
                      <Input
                        type="textarea"
                        rows="4"
                        value={adminResponse}
                        onChange={(e) => setAdminResponse(e.target.value)}
                        placeholder="Type your response here..."
                      />
                    </FormGroup>
                    <button
                      className="btn-style-new"
                      color="primary"
                      onClick={handleAddResponse}
                      disabled={!adminResponse || adminResponse.trim().length === 0}
                      block
                    >
                      <i className="fa fa-send mr-2"></i>
                      {selectedFeedback.adminResponse?.message ? 'Update Response' : 'Send Response'}
                    </button>
                  </CardBody>
                </Card>

                {/* Timeline */}
                <Card className="border-0 shadow-sm">
                  <CardBody>
                    <h6 className="font-weight-bold mb-3">
                      <i className="fa fa-history mr-2"></i>Timeline
                    </h6>
                    <div className="timeline">
                      <div className="timeline-item">
                        <i className="fa fa-circle text-success"></i>
                        <div>
                          <strong>Created</strong>
                          <br />
                          <small className="text-muted">
                            {moment(selectedFeedback.createdAt).format('MMM DD, YYYY [at] hh:mm A')}
                          </small>
                        </div>
                      </div>
                      {selectedFeedback.updatedAt && selectedFeedback.updatedAt !== selectedFeedback.createdAt && (
                        <div className="timeline-item">
                          <i className="fa fa-circle text-info"></i>
                          <div>
                            <strong>Last Updated</strong>
                            <br />
                            <small className="text-muted">
                              {moment(selectedFeedback.updatedAt).format('MMM DD, YYYY [at] hh:mm A')}
                            </small>
                          </div>
                        </div>
                      )}
                      {selectedFeedback.resolvedAt && (
                        <div className="timeline-item">
                          <i className="fa fa-circle text-success"></i>
                          <div>
                            <strong>Resolved</strong>
                            <br />
                            <small className="text-muted">
                              {moment(selectedFeedback.resolvedAt).format('MMM DD, YYYY [at] hh:mm A')}
                            </small>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </div>
            ) : (
              <div className="text-center py-5">
                <p className="text-muted">No feedback selected</p>
              </div>
            )}
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default FeedbackSidebar;
